import React, { useState, useEffect } from 'react';
import { adAnalyticsService, AdAnalyticsMetrics } from '../../lib/adAnalytics';
import { useGoogleAds } from '../../hooks/useGoogleAds';

/**
 * Debug component for ad analytics - only shown in development
 */
export const AdAnalyticsDebug: React.FC = () => {
  const [metrics, setMetrics] = useState<AdAnalyticsMetrics | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const { isEnabled, isAvailable, metrics: adsMetrics, error } = useGoogleAds();

  useEffect(() => {
    const updateMetrics = () => {
      setMetrics(adAnalyticsService.getMetrics());
    };

    // Update metrics immediately
    updateMetrics();

    // Update metrics every 5 seconds
    const interval = setInterval(updateMetrics, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleClearMetrics = () => {
    adAnalyticsService.clearEvents();
    setMetrics(adAnalyticsService.getMetrics());
  };

  const handleExportEvents = () => {
    const events = adAnalyticsService.exportEvents();
    const blob = new Blob([events], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ad-analytics-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!import.meta.env.DEV) {
    return null;
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mt-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-gray-800 flex items-center gap-2">
          📊 Ad Analytics Debug
          {isEnabled && (
            <span className={`px-2 py-1 text-xs rounded-full ${
              isAvailable ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
            }`}>
              {isAvailable ? 'Active' : 'Loading'}
            </span>
          )}
          {!isEnabled && (
            <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">
              Disabled
            </span>
          )}
        </h4>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-sm text-gray-500 hover:text-gray-700"
        >
          {isExpanded ? 'Collapse' : 'Expand'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-2 mb-3">
          <p className="text-red-800 text-xs">Error: {error}</p>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-2 mb-3 text-xs">
        <div className="bg-blue-50 p-2 rounded text-center">
          <div className="font-semibold text-blue-800">{metrics?.totalImpressions || 0}</div>
          <div className="text-blue-600">Impressions</div>
        </div>
        <div className="bg-green-50 p-2 rounded text-center">
          <div className="font-semibold text-green-800">{metrics?.totalClicks || 0}</div>
          <div className="text-green-600">Clicks</div>
        </div>
        <div className="bg-red-50 p-2 rounded text-center">
          <div className="font-semibold text-red-800">{metrics?.totalErrors || 0}</div>
          <div className="text-red-600">Errors</div>
        </div>
        <div className="bg-purple-50 p-2 rounded text-center">
          <div className="font-semibold text-purple-800">
            {metrics?.clickThroughRate.toFixed(1) || 0}%
          </div>
          <div className="text-purple-600">CTR</div>
        </div>
      </div>

      {isExpanded && metrics && (
        <div className="space-y-3">
          {/* Detailed Metrics */}
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div>
              <h5 className="font-medium text-gray-700 mb-2">Performance</h5>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span>Avg Load Time:</span>
                  <span>{metrics.averageLoadTime.toFixed(0)}ms</span>
                </div>
                <div className="flex justify-between">
                  <span>Error Rate:</span>
                  <span>{metrics.errorRate.toFixed(1)}%</span>
                </div>
                <div className="flex justify-between">
                  <span>Click Rate:</span>
                  <span>{metrics.clickThroughRate.toFixed(1)}%</span>
                </div>
              </div>
            </div>

            <div>
              <h5 className="font-medium text-gray-700 mb-2">By Type</h5>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span>Card Ads:</span>
                  <span>{metrics.impressionsByType['treasure-card'] || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>Sidebar Ads:</span>
                  <span>{metrics.impressionsByType['sidebar'] || 0}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Google Ads Service Metrics */}
          {adsMetrics && (
            <div className="border-t pt-3">
              <h5 className="font-medium text-gray-700 mb-2">Google Ads Service</h5>
              <div className="grid grid-cols-2 gap-4 text-xs">
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span>Service Impressions:</span>
                    <span>{adsMetrics.impressions}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Service Clicks:</span>
                    <span>{adsMetrics.clicks}</span>
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span>Service Errors:</span>
                    <span>{adsMetrics.errors}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Service Load Time:</span>
                    <span>{adsMetrics.loadTime.toFixed(0)}ms</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-3 border-t">
            <button
              onClick={handleClearMetrics}
              className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-xs hover:bg-gray-200"
            >
              Clear Metrics
            </button>
            <button
              onClick={handleExportEvents}
              className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200"
            >
              Export Events
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdAnalyticsDebug;